import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { defaultLang, supportedLangs } from "./i18nConfig";
import { fetchTranslations } from "./i18nAPI";

// Initialize language from localStorage or use default
let _defaultLang = defaultLang;
let translations = {};

try {
  const savedLang = localStorage.getItem("lang");
  _defaultLang = savedLang && Object.keys(supportedLangs).includes(savedLang) ? savedLang : defaultLang;
  translations = fetchTranslations(_defaultLang);
} catch (e) {
  console.error("Error initializing i18n:", e);
  translations = fetchTranslations(defaultLang);
}

const initialState = {
  status: "idle",
  error: null,
  lang: _defaultLang,
  previousLang: null,
  supportedLangs: { ...supportedLangs },
  translations: translations,
  isInitialized: false,
};

export const setLangAsync = createAsyncThunk("i18n/setLangAsync", async (lang, { getState, dispatch, rejectWithValue }) => {
  try {
    const currentState = getState().i18n;
    const resolvedLang = lang || currentState.lang;

    // Validate language is supported
    if (!Object.keys(supportedLangs).includes(resolvedLang)) {
      throw new Error(`Unsupported language: ${resolvedLang}`);
    }

    // Store previous language for potential rollback
    dispatch(i18nSlice.actions.setPreviousLang(currentState.lang));

    // Fetch translations
    const translations = await fetchTranslations(resolvedLang);

    // Update language in reducer
    dispatch(i18nSlice.actions.setLang(resolvedLang));

    // Save to localStorage
    try {
      localStorage.setItem("lang", resolvedLang);
    } catch (storageError) {
      console.warn("Could not save language to localStorage:", storageError);
    }

    return {
      lang: resolvedLang,
      translations,
    };
  } catch (error) {
    console.error("Error setting language:", error);
    return rejectWithValue(error.message);
  }
});

export const initializeI18n = createAsyncThunk("i18n/initialize", async (_, { dispatch }) => {
  try {
    const savedLang = localStorage.getItem("lang") || defaultLang;
    await dispatch(setLangAsync(savedLang));
    return true;
  } catch (error) {
    console.error("Error initializing i18n:", error);
    return false;
  }
});

export const i18nSlice = createSlice({
  name: "i18n",
  initialState,
  reducers: {
    setLang: (state, action) => {
      state.lang = action.payload;
    },
    setPreviousLang: (state, action) => {
      state.previousLang = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state, action) => {
      state.isInitialized = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setLangAsync.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(setLangAsync.fulfilled, (state, action) => {
        state.translations = action.payload.translations;
        state.lang = action.payload.lang;
        state.status = "idle";
        state.error = null;
        state.isInitialized = true;
      })
      .addCase(setLangAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        // Rollback to previous language if available
        if (state.previousLang) {
          state.lang = state.previousLang;
          state.translations = fetchTranslations(state.previousLang);
        }
      })
      .addCase(initializeI18n.fulfilled, (state, action) => {
        state.isInitialized = action.payload;
      });
  },
});

export const { clearError, setInitialized } = i18nSlice.actions;
export default i18nSlice.reducer;
